import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { MESSAGES, type Locale, type Messages } from "./messages";

const STORAGE_KEY = "tn-locale";

// Default detection (design decision D9): Spanish for es-* locales (Latin
// America + Spain effectively), English for everything else. A manual header
// toggle overrides the detection and persists in localStorage.
function detectLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "es" || stored === "en") return stored;
  } catch {
    // localStorage unavailable (private mode, SSR) — fall through
  }
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  return languages.some((lang) => lang?.toLowerCase().startsWith("es")) ? "es" : "en";
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Messages;
}

const LocaleContext = createContext<LocaleState | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleState>(
    () => ({
      locale,
      t: MESSAGES[locale],
      setLocale: (next: Locale) => {
        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // non-fatal — the toggle still works for this session
        }
        setLocaleState(next);
      },
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleState {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within <LocaleProvider>");
  return ctx;
}
